import { swissCantonLabel } from '@/lib/countries';
import { getCategoryLabel } from '@/lib/industry-categories';
import {
  personAgeRanges,
  normalizeFilterParams,
  type RangeDefinition,
} from '@/lib/search-filters';
import { controlLock, type FilterTier } from '@/lib/search-gating';
import type { FilterParams } from './CompanySearchSidebar';
import { RECHTSFORM_LABEL_OPTIONS } from './searchConstants';

/**
 * Which filters a search actually applied, derived from the filter object.
 *
 * There were three hand-maintained lists of "the filters that exist" — the
 * header's active-filter badge, the `hasFilters` gate that decides whether a
 * search is worth logging, and the analytics payload — and each had drifted
 * from `FilterParams` independently. The logging one was the worst: it knew
 * about 13 of 28 filters, so a search narrowed only by Rechtsform, membership,
 * Bilanzsumme or register number was **not logged at all**. Statistics built
 * on that are biased, not merely incomplete, which is worse.
 *
 * `FILTER_SPEC` is typed as a total map over `FilterParams`, so adding a
 * filter without describing it here is a compile error rather than a silent
 * hole in the analytics.
 *
 * Since saved searches, each entry also carries `chip` — how the filter reads
 * on a saved-search card — and `order`, the fixed presentation sequence. The
 * same exhaustiveness now protects the card: a 34th filter cannot ship
 * invisible, which on a saved search would be worse than in analytics,
 * because the user would be looking at a summary that omits part of what
 * their search actually does.
 */
type FilterEntry = {
  /** Does this search apply the filter at all? */
  active: (f: FilterParams) => boolean;
  /** All fields reset when removing this chip. */
  clear: (keyof FilterParams)[];
  /** Fixed slot in the chip row, so the same filters always read the same. */
  order: number;
  /**
   * The chip text, or null when this key is folded into another chip
   * (`revenueIncludeNull` is a modifier printed on the revenue chip;
   * `foundedAtTo` shares one chip with `foundedAtFrom`).
   */
  chip: (f: FilterParams, ctx: ChipContext) => string | null;
};

type FilterSpec = {
  [K in keyof Required<FilterParams>]: FilterEntry;
};

/** i18next's `t`, narrowed to the one call shape the chips use. */
export type ChipTranslator = (
  key: string,
  options: { defaultValue: string },
) => string;

export type ChipContext = { t: ChipTranslator; lang: string };

export type FilterChip = {
  key: keyof FilterParams;
  label: string;
};

const nonEmpty = (v: string | undefined) => !!v && v.trim() !== '';
const anyOf = (v: string[] | undefined) => !!v && v.length > 0;

// ── value formatting ────────────────────────────────────────────────────────

/**
 * A number split into its scaled quantity and unit, so a span sharing one
 * unit can print it once ("1–10 Mio. €" rather than "1 Mio. €–10 Mio. €").
 * Compact by design: a chip row has no room for grouped digits, and the
 * financial bounds can span many orders of magnitude.
 */
function moneyParts(n: number, lang: string): { q: string; unit: string } {
  const de = lang !== 'en';
  const sign = n < 0 ? '−' : '';
  const abs = Math.abs(n);
  const q = (v: number) =>
    `${sign}${v % 1 === 0 ? String(v) : v.toFixed(1).replace('.', de ? ',' : '.')}`;
  if (abs >= 1e9) return { q: q(abs / 1e9), unit: de ? 'Mrd. €' : 'bn €' };
  if (abs >= 1e6) return { q: q(abs / 1e6), unit: de ? 'Mio. €' : 'm €' };
  if (abs >= 1e3) return { q: q(abs / 1e3), unit: de ? 'Tsd. €' : 'k €' };
  return { q: q(abs), unit: '€' };
}

type ValueFormatter = {
  one: (n: number, lang: string) => string;
  /** Both bounds at once, so a shared unit is printed once. */
  pair: (a: number, b: number, lang: string) => string;
};

const money: ValueFormatter = {
  one: (n, lang) => {
    const { q, unit } = moneyParts(n, lang);
    return `${q} ${unit}`;
  },
  pair: (a, b, lang) => {
    const lo = moneyParts(a, lang);
    const hi = moneyParts(b, lang);
    return lo.unit === hi.unit
      ? `${lo.q}–${hi.q} ${hi.unit}`
      : `${lo.q} ${lo.unit}–${hi.q} ${hi.unit}`;
  },
};

const plain: ValueFormatter = {
  one: (n) => String(n),
  pair: (a, b) => `${a}–${b}`,
};

/**
 * One selection of range buckets as a single readable span.
 *
 * Adjacent buckets merge (`1m-5m` + `5m-10m` → "1–10 Mio. €") because that is
 * how the user thinks of it. A non-contiguous pick genuinely cannot be
 * written as one span, so it degrades to a count rather than lying about the
 * bounds — the same reason the backend carries discrete `spans[]` instead of
 * one envelope.
 */
function rangeLabel(
  ids: string[],
  table: RangeDefinition[],
  fmt: ValueFormatter,
  ctx: ChipContext,
): string {
  const indices = ids
    .map((id) => table.findIndex((r) => r.id === id))
    .filter((i) => i >= 0)
    .sort((a, b) => a - b);
  if (indices.length === 0) return '';

  const contiguous = indices.every((v, i) => i === 0 || v === indices[i - 1] + 1);
  if (!contiguous) {
    return `${indices.length} ${ctx.t('search.chips.ranges', { defaultValue: 'ranges' })}`;
  }

  const { min } = table[indices[0]];
  const { max } = table[indices[indices.length - 1]];
  if (min == null && max == null) return '';
  if (min == null) {
    return `${ctx.t('search.chips.upTo', { defaultValue: 'up to' })} ${fmt.one(max as number, ctx.lang)}`;
  }
  if (max == null) {
    return `${ctx.t('search.chips.from', { defaultValue: 'from' })} ${fmt.one(min, ctx.lang)}`;
  }
  return fmt.pair(min, max, ctx.lang);
}

/**
 * Age buckets are stored as the *birth-year* window they select, so the
 * human number is recovered by subtracting from the current year rather than
 * by parsing the bucket id.
 */
function ageLabel(ids: string[], ctx: ChipContext): string {
  const year = new Date().getFullYear();
  const table = personAgeRanges(year);
  const asAges: RangeDefinition[] = table.map((r) => ({
    id: r.id,
    min: r.max == null ? null : year - r.max,
    max: r.min == null ? null : year - r.min,
  }));
  return rangeLabel(ids, asAges, plain, ctx);
}

/** `["Bayern","Hessen","Berlin"] → "Bayern +2"`. The first value plus a count
 *  reads at a glance; a comma list does not survive a 4-chip row. */
function firstPlusMore(values: string[]): string {
  if (values.length === 0) return '';
  const [head, ...rest] = values;
  return rest.length ? `${head} +${rest.length}` : head;
}

const labelled = (label: string, value: string) => (value ? `${label} ${value}` : label);

/** Tri-state booleans read as words — "Nicht familiengeführt" is a real
 *  filter, and rendering it the same as the positive case would invert the
 *  meaning of the card. */
function triState(
  value: boolean | undefined,
  yes: string,
  no: string,
  ctx: ChipContext,
  keyBase: string,
): string | null {
  if (value === undefined) return null;
  return value
    ? ctx.t(`search.chips.${keyBase}Yes`, { defaultValue: yes })
    : ctx.t(`search.chips.${keyBase}No`, { defaultValue: no });
}

const RECHTSFORM_BY_CODE = new Map<string, string>(
  RECHTSFORM_LABEL_OPTIONS.flatMap((o) => o.codes.map((c) => [c, o.label] as const)),
);

function foundedLabel(f: FilterParams, ctx: ChipContext): string {
  const from = f.foundedAtFrom;
  const to = f.foundedAtTo;
  const founded = ctx.t('search.chips.founded', { defaultValue: 'Founded' });
  if (from && to) return `${founded} ${from}–${to}`;
  if (from) {
    return `${founded} ${ctx.t('search.chips.from', { defaultValue: 'from' })} ${from}`;
  }
  return `${founded} ${ctx.t('search.chips.upTo', { defaultValue: 'up to' })} ${to}`;
}

function metricLabel(min: number | undefined, max: number | undefined, key: string, label: string, fmt: ValueFormatter, ctx: ChipContext): string {
  const value = min !== undefined && max !== undefined ? fmt.pair(min, max, ctx.lang)
    : min !== undefined ? `${ctx.t('search.chips.from', { defaultValue: 'from' })} ${fmt.one(min, ctx.lang)}`
    : max !== undefined ? `${ctx.t('search.chips.upTo', { defaultValue: 'up to' })} ${fmt.one(max, ctx.lang)}` : '';
  return labelled(ctx.t(`search.chips.${key}`, { defaultValue: label }), value);
}

const FILTER_SPEC: FilterSpec = {
  q: {
    clear: ['q'],
    active: (f) => nonEmpty(f.q),
    order: 0,
    chip: (f) => `„${f.q!.trim()}“`,
  },
  personName: {
    clear: ['personName'],
    active: (f) => nonEmpty(f.personName),
    order: 1,
    chip: (f, c) =>
      labelled(c.t('search.chips.person', { defaultValue: 'Person' }), f.personName!.trim()),
  },
  city: {
    clear: ['city'],
    active: (f) => anyOf(f.city),
    order: 2,
    chip: (f) => firstPlusMore(f.city!),
  },
  Bundesland: {
    clear: ['Bundesland'],
    active: (f) => anyOf(f.Bundesland),
    order: 3,
    chip: (f, c) => firstPlusMore(f.Bundesland!.map((region) => swissCantonLabel(region, c.lang))),
  },
  industries: {
    clear: ['industries'],
    active: (f) => anyOf(f.industries),
    order: 4,
    chip: (f, c) =>
      firstPlusMore(f.industries!.map((slug) => getCategoryLabel(slug, c.lang) || slug)),
  },
  naceCodes: {
    clear: ['naceCodes'],
    active: (f) => anyOf(f.naceCodes),
    order: 5,
    chip: (f, c) =>
      labelled(c.t('search.chips.wz', { defaultValue: 'WZ' }), firstPlusMore(f.naceCodes!)),
  },
  tags: {
    clear: ['tags'],
    active: (f) => anyOf(f.tags),
    order: 6,
    chip: (f, c) =>
      labelled(c.t('search.chips.keyword', { defaultValue: 'Keyword' }), firstPlusMore(f.tags!)),
  },
  rechtsformCodes: {
    clear: ['rechtsformCodes'],
    active: (f) => anyOf(f.rechtsformCodes),
    order: 7,
    chip: (f) =>
      // Codes are numeric (`221110`), so the lookup is not cosmetic — the raw
      // value would be unreadable on the card.
      firstPlusMore(f.rechtsformCodes!.map((c) => RECHTSFORM_BY_CODE.get(c) ?? c)),
  },
  revenueMin: {
    clear: ['revenueMin', 'revenueMax', 'revenueIncludeNull'],
    active: (f) => f.revenueMin !== undefined,
    order: 8,
    chip: (f, c) => {
      const base = metricLabel(f.revenueMin, f.revenueMax, 'revenue', 'Revenue', money, c);
      return f.revenueIncludeNull ? `${base} ${c.t('search.chips.inclNoData', { defaultValue: '(incl. no data)' })}` : base;
    },
  },
  revenueMax: {
    clear: ['revenueMin', 'revenueMax', 'revenueIncludeNull'],
    active: (f) => f.revenueMax !== undefined,
    order: 8,
    chip: (f, c) => {
      if (f.revenueMin !== undefined) return null;
      const base = metricLabel(f.revenueMin, f.revenueMax, 'revenue', 'Revenue', money, c);
      return f.revenueIncludeNull ? `${base} ${c.t('search.chips.inclNoData', { defaultValue: '(incl. no data)' })}` : base;
    },
  },
  revenueIncludeNull: {
    clear: ['revenueMin', 'revenueMax', 'revenueIncludeNull'],
    active: (f) => f.revenueIncludeNull === true && (f.revenueMin !== undefined || f.revenueMax !== undefined),
    order: 9,
    chip: () => null,
  },
  aktivaMin: {
    clear: ['aktivaMin', 'aktivaMax'],
    active: (f) => f.aktivaMin !== undefined,
    order: 10,
    chip: (f, c) => metricLabel(f.aktivaMin, f.aktivaMax, 'totalAssets', 'Total assets', money, c),
  },
  aktivaMax: {
    clear: ['aktivaMin', 'aktivaMax'],
    active: (f) => f.aktivaMax !== undefined,
    order: 10,
    chip: (f, c) => f.aktivaMin !== undefined ? null : metricLabel(f.aktivaMin, f.aktivaMax, 'totalAssets', 'Total assets', money, c),
  },
  profitMin: {
    clear: ['profitMin', 'profitMax'],
    active: (f) => f.profitMin !== undefined,
    order: 11,
    chip: (f, c) => metricLabel(f.profitMin, f.profitMax, 'profit', 'Profit', money, c),
  },
  profitMax: {
    clear: ['profitMin', 'profitMax'],
    active: (f) => f.profitMax !== undefined,
    order: 11,
    chip: (f, c) => f.profitMin !== undefined ? null : metricLabel(f.profitMin, f.profitMax, 'profit', 'Profit', money, c),
  },
  employeesMin: {
    clear: ['employeesMin', 'employeesMax'],
    active: (f) => f.employeesMin !== undefined,
    order: 12,
    chip: (f, c) => metricLabel(f.employeesMin, f.employeesMax, 'employees', 'Employees', plain, c),
  },
  employeesMax: {
    clear: ['employeesMin', 'employeesMax'],
    active: (f) => f.employeesMax !== undefined,
    order: 12,
    chip: (f, c) => f.employeesMin !== undefined ? null : metricLabel(f.employeesMin, f.employeesMax, 'employees', 'Employees', plain, c),
  },
  ageRanges: {
    clear: ['ageRanges'],
    active: (f) => anyOf(f.ageRanges),
    order: 13,
    chip: (f, c) =>
      labelled(c.t('search.chips.avgAge', { defaultValue: 'Avg. age' }), ageLabel(f.ageRanges!, c)),
  },
  foundedAtFrom: {
    clear: ['foundedAtFrom', 'foundedAtTo'],
    active: (f) => nonEmpty(f.foundedAtFrom),
    order: 14,
    chip: (f, c) => foundedLabel(f, c),
  },
  foundedAtTo: {
    clear: ['foundedAtFrom', 'foundedAtTo'],
    active: (f) => nonEmpty(f.foundedAtTo),
    order: 15,
    // One chip covers both bounds; it renders here only when there is no
    // lower bound to have rendered it already.
    chip: (f, c) => (nonEmpty(f.foundedAtFrom) ? null : foundedLabel(f, c)),
  },
  legalStatus: {
    clear: ['legalStatus'],
    active: (f) => anyOf(f.legalStatus),
    order: 16,
    chip: (f, c) =>
      firstPlusMore(
        f.legalStatus!.map((v) =>
          c.t(`search.sidebar.legalStatusOptions.${LEGAL_STATUS_KEY[v] ?? v}`, {
            defaultValue: v,
          }),
        ),
      ),
  },
  country: {
    clear: ['country'],
    // Every country is the default scope (no chip — one that is always there
    // says nothing). Only a search narrowed to one country, from an old link
    // or a saved search, shows one, so it can be cleared.
    active: (f) => f.country === 'DE' || f.country === 'CH' || f.country === 'AT',
    order: 35,
    chip: (f, c) =>
      f.country === 'CH'
        ? c.t('search.chips.countryCH', { defaultValue: 'Switzerland' })
        : f.country === 'AT'
          ? c.t('search.chips.countryAT', { defaultValue: 'Austria' })
          : c.t('search.sidebar.countryGermany', { defaultValue: 'Germany' }),
  },
  companySize: {
    clear: ['companySize'],
    active: (f) => anyOf(f.companySize),
    order: 33,
    chip: (f, c) =>
      labelled(
        c.t('search.chips.companySize', { defaultValue: 'Size' }),
        firstPlusMore(
          f.companySize!.map((v) =>
            c.t(`search.sidebar.companySizeOptions.${v}`, { defaultValue: v }),
          ),
        ),
      ),
  },
  tenderRole: {
    clear: ['tenderRole'],
    active: (f) => anyOf(f.tenderRole),
    order: 17,
    chip: (f, c) =>
      labelled(
        c.t('search.chips.tender', { defaultValue: 'Tenders' }),
        firstPlusMore(
          f.tenderRole!.map((v) =>
            c.t(`search.sidebar.tenderRoleOptions.${v}`, { defaultValue: v }),
          ),
        ),
      ),
  },
  familyManaged: {
    clear: ['familyManaged'],
    active: (f) => f.familyManaged !== undefined,
    order: 18,
    chip: (f, c) => triState(f.familyManaged, 'Family-managed', 'Not family-managed', c, 'family'),
  },
  founderLed: {
    clear: ['founderLed'],
    active: (f) => f.founderLed !== undefined,
    order: 19,
    chip: (f, c) => triState(f.founderLed, 'Founder-led', 'Not founder-led', c, 'founderLed'),
  },
  successionRisk: {
    clear: ['successionRisk'],
    active: (f) => f.successionRisk !== undefined,
    order: 20,
    chip: (f, c) =>
      triState(f.successionRisk, 'Succession risk', 'No succession risk', c, 'succession'),
  },
  memberships: {
    clear: ['memberships'],
    active: (f) => anyOf(f.memberships),
    order: 21,
    chip: (f) => firstPlusMore(f.memberships!),
  },
  hasWebsite: {
    clear: ['hasWebsite'],
    active: (f) => f.hasWebsite === true,
    order: 22,
    chip: (_f, c) => c.t('search.chips.hasWebsite', { defaultValue: 'With website' }),
  },
  hasEmail: {
    clear: ['hasEmail'],
    active: (f) => f.hasEmail === true,
    order: 23,
    chip: (_f, c) => c.t('search.chips.hasEmail', { defaultValue: 'With email' }),
  },
  hasYearlyMetadata: {
    clear: ['hasYearlyMetadata'],
    active: (f) => f.hasYearlyMetadata === true,
    order: 24,
    chip: (_f, c) => c.t('search.chips.hasFilings', { defaultValue: 'With filings' }),
  },
  financialYear: {
    clear: ['financialYear'],
    active: (f) => f.financialYear != null,
    order: 25,
    chip: (f, c) =>
      labelled(
        c.t('search.chips.filingYear', { defaultValue: 'Filing from' }),
        String(f.financialYear),
      ),
  },
  cpvCode: {
    clear: ['cpvCode'],
    active: (f) => nonEmpty(f.cpvCode),
    order: 26,
    chip: (f, c) => labelled(c.t('search.chips.cpv', { defaultValue: 'CPV' }), f.cpvCode!.trim()),
  },
  registerType: {
    clear: ['registerType'],
    active: (f) => nonEmpty(f.registerType),
    order: 27,
    chip: (f) => f.registerType!.trim(),
  },
  registerId: {
    clear: ['registerId'],
    active: (f) => nonEmpty(f.registerId),
    order: 28,
    chip: (f, c) =>
      labelled(c.t('search.chips.registerNo', { defaultValue: 'No.' }), f.registerId!.trim()),
  },
  registerCourt: {
    clear: ['registerCourt'],
    active: (f) => nonEmpty(f.registerCourt),
    order: 29,
    chip: (f, c) =>
      labelled(c.t('search.chips.court', { defaultValue: 'Court' }), swissCantonLabel(f.registerCourt!.trim(), c.lang)),
  },
  euid: {
    clear: ['euid'],
    active: (f) => nonEmpty(f.euid),
    order: 30,
    chip: (f) => f.euid!.trim(),
  },
  leiCode: {
    clear: ['leiCode'],
    active: (f) => nonEmpty(f.leiCode),
    order: 31,
    chip: (f, c) => labelled(c.t('search.chips.lei', { defaultValue: 'LEI' }), f.leiCode!.trim()),
  },
  ustIdnr: {
    clear: ['ustIdnr'],
    active: (f) => nonEmpty(f.ustIdnr),
    order: 32,
    chip: (f, c) =>
      labelled(c.t('search.chips.vatId', { defaultValue: 'VAT ID' }), f.ustIdnr!.trim()),
  },
};

/** The sidebar's i18n keys are camelCase while the filter values are the
 *  backend's snake_case, so the two need bridging for the status chips. */
const LEGAL_STATUS_KEY: Record<string, string> = {
  active: 'active',
  in_liquidation: 'inLiquidation',
  insolvent: 'insolvent',
  dissolved: 'dissolved',
  deleted: 'deleted',
};

export const ALL_FILTER_KEYS = Object.keys(FILTER_SPEC) as (keyof FilterParams)[];

/**
 * Controls whose gating is decided by another control.
 *
 * `revenueIncludeNull` widens the revenue interval; with revenue stripped it
 * applies to nothing, so it must follow revenue rather than count as an
 * ungated filter of its own.
 */
const TIER_ALIAS: Partial<Record<keyof FilterParams, string>> = {
  revenueIncludeNull: 'revenueMin',
};

/** True when `tier` may not use this control, so the server drops it. */
function strippedFor(key: keyof FilterParams, tier: FilterTier): boolean {
  return controlLock(TIER_ALIAS[key] ?? key, tier) !== null;
}

/**
 * The keys of every filter this search applies.
 *
 * `q` is the search term rather than a filter — the header shows it in the
 * input, not as a badge — so it is excluded unless asked for. Analytics wants
 * it included, since "did the keyword find anything" is exactly the question
 * the zero-result reporting needs to answer.
 */
export function activeFilterKeys(
  f: FilterParams,
  {
    includeQuery = false,
    tier,
  }: { includeQuery?: boolean; tier?: FilterTier } = {},
): string[] {
  return ALL_FILTER_KEYS.filter(
    (k) =>
      (includeQuery || k !== 'q') &&
      FILTER_SPEC[k].active(f) &&
      !(tier && strippedFor(k, tier)),
  );
}

/**
 * How many visible chips are applied — drives the header badge.
 * Paired bounds and modifiers share the chip that describes them.
 *
 * Pass the visitor's tier so a URL carrying a filter they may not use (a
 * shared link, a stale bookmark) doesn't claim to be narrowing results the
 * server has already stripped.
 */
export function activeFilterCount(f: FilterParams, tier?: FilterTier): number {
  const ctx: ChipContext = { t: (_key, options) => options.defaultValue, lang: 'en' };
  const active = new Set(activeFilterKeys(f, { tier }));
  return ALL_FILTER_KEYS.filter((key) => active.has(key) && FILTER_SPEC[key].chip(f, ctx) !== null).length;
}

/** True when the search narrows anything at all, keyword included. */
export function hasAnyFilter(f: FilterParams, tier?: FilterTier): boolean {
  return activeFilterKeys(f, { includeQuery: true, tier }).length > 0;
}

/**
 * The applied filters and their values, for the analytics payload.
 *
 * Arrays are joined rather than nested so the stored JSON stays flat and
 * `meta::jsonb ->> key` reads as a plain string in aggregation queries.
 *
 * With a `tier`, only the filters the server actually honoured are recorded
 * — otherwise a guest's `?city=Berlin` would be logged against a result
 * count produced without it, which is exactly the correlation the
 * zero-result reporting exists to trust.
 */
export function activeFilterValues(
  f: FilterParams,
  tier?: FilterTier,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const key of activeFilterKeys(f, { includeQuery: true, tier })) {
    const raw = (f as unknown as Record<string, unknown>)[key];
    out[key] = Array.isArray(raw) ? raw.join(',') : String(raw);
  }
  return out;
}

/**
 * The filter set as a short, ordered list of human-readable chips.
 *
 * This is what makes a saved search recognisable: a name alone ("Bayern") is
 * ambiguous a week later, and the raw filter object is unreadable. One
 * implementation serves the save dialog's preview, the card on the manage
 * page and the auto-generated name — so the preview can never promise
 * something the card then omits.
 *
 * Chips whose value is another chip's modifier return null (see
 * `revenueIncludeNull`, `foundedAtTo`) and are dropped here.
 */
export function describeFilters(f: FilterParams, ctx: ChipContext): FilterChip[] {
  return ALL_FILTER_KEYS.filter((k) => FILTER_SPEC[k].active(f))
    .sort((a, b) => FILTER_SPEC[a].order - FILTER_SPEC[b].order)
    .map((key) => ({ key, label: FILTER_SPEC[key].chip(f, ctx) }))
    .filter((c): c is FilterChip => !!c.label && c.label.trim() !== '');
}

/**
 * A default name for a search the user just built.
 *
 * Every competitor starts you at an empty field, which is where saved
 * searches go to become "Suche 1", "Suche 2", "asdf". Three chips is enough
 * to recognise the search and short enough to stay on one line.
 */
export function suggestSearchName(f: FilterParams, ctx: ChipContext): string {
  const chips = describeFilters(f, ctx);
  if (chips.length === 0) {
    return ctx.t('search.chips.allCompanies', { defaultValue: 'All companies' });
  }
  const name = chips.slice(0, 3).map((c) => c.label).join(' · ');
  return name.length > 60 ? `${name.slice(0, 59).trimEnd()}…` : name;
}

/** Reset the complete chip, including paired bounds and modifiers. */
export function removeFilter(f: FilterParams, key: keyof FilterParams): FilterParams {
  const defaults = normalizeFilterParams({});
  const reset = Object.fromEntries(FILTER_SPEC[key].clear.map((field) => [field, defaults[field]]));
  return { ...f, ...reset };
}

/** Query and country persist; callers may supply only the chips their tier shows. */
export function clearAllFilters(f: FilterParams, keys: readonly (keyof FilterParams)[] = ALL_FILTER_KEYS): FilterParams {
  return keys.filter((key) => key !== 'q' && key !== 'country').reduce(removeFilter, f);
}
